import isEqual from 'lodash/isEqual'

import { Types as AppTypes } from '../actions/app'
import { Types as DeviceTypes } from '../actions/device'
import { Types as SocketTypes } from '../actions/socket'
import config from '../config'
import { DateUtils, StoreUtils, ArrayUtils } from '../utils'
import { Types as StoreTypes, StoresSearchModes } from '../actions/stores'

const STATE_KEY = 'stores'

const initialPagination = {
    currentPage: 0,
    perPage: 0,
    total: 0,
    from: 0,
    to: 0,
}

const initialDevicesMap = {}

const { value: initialSearchMode } = StoresSearchModes.searchByStore || {}

const initialFilters = {
    countriesToFilter: [],
    groupsToFilter: [1],
    migrated: true,
    mode: initialSearchMode,
    showEmptyStores: false,
    statusToFilter: [],
}

const initialState = {
    devices: {
        ...initialDevicesMap,
    },
    devicesInit: false,
    error: null,
    isWorking: false,
    list: [],
    pagination: {
        ...initialPagination,
    },
    selectedDevice: null,
    selectedStore: null,
    screenshots: [],
    search: '',
    filters: {
        ...initialFilters,
    },
    totalDevices: 0,
    totalFilteredDevices: 0,
}

function areFiltersAtDefaultValue(filters, search = '') {
    return isEqual(filters, initialFilters) && search === ''
}

function isDeviceOnline({ isOnline, last_contact: lastContact }) {
    return isOnline || (lastContact && DateUtils.now() - new Date(lastContact).getTime() < config.map.timeToBeOffline)
}

/**
 * 
 * @param {object} params 
 * @param {Device[]} params.devices
 * @returns 
 */
function storeOfflinePercentage({ devices = [] }) {
    return devices.length ? devices.map((device) => (isDeviceOnline(device) ? 0 : 1)).reduce((a, b) => a + b, 0) / devices.length || 0 : 1
}

function formatRawDeviceInfo({
    last_contact,
    last_download,
    layer_extra,
    cdd_last_online,
    cdd_last_failed_to_connect,
    ...device
}) {
    return {
        ...device,
        isOnline: false, 
        last_contact: last_contact && DateUtils.UTCDateToTimestamp(last_contact),
        last_download: last_download && DateUtils.UTCDateToTimestamp(last_download),
        layer_extra: JSON.parse(layer_extra || '{}'),
        cdd_last_online: cdd_last_online && DateUtils.UTCDateToTimestamp(cdd_last_online),
        cdd_last_failed_to_connect: cdd_last_failed_to_connect && DateUtils.UTCDateToTimestamp(cdd_last_failed_to_connect),
    }
}

function formatStoreAttribute({ id, attribute_key, value }) {
    return {
        attributeKey: attribute_key,
        id,
        value,
    }
}

function formatRawStoreInfo({ attributes = [], chain_id, devices, lat, lng, notes = [], ...store }) {
    if (devices)
        return {
            ...store,
            attributes: attributes.map(formatStoreAttribute),
            coordinates: [parseFloat(lat), parseFloat(lng)],
            notes: notes.map((note) => ({ sticky: false, created_at: 0, ...note })).sort(StoreUtils.compareNoteDesc),
            devices: devices.map(formatRawDeviceInfo),
        }
    return {
        ...store,
        chain_id: parseInt(chain_id, 10),
        coordinates: [parseFloat(lat), parseFloat(lng)],
    }
}

function updateDeviceStatusInfo(devices, update, store = {}) {
    const { id, last_contact, last_download } = update
    const { code: storeCode } = store

    if (devices[id]) {
        // eslint-disable-next-line no-param-reassign
        devices[id] = {
            ...devices[id],
            lastContact: last_contact,
            lastDownload: last_download,
        }
    } else {
        // eslint-disable-next-line no-param-reassign
        devices[id] = {
            isOnline: false,
            lastContact: last_contact,
            lastDownload: last_download,
            storeCode,
        }
    }
}

function reducer(state = initialState, { type, payload = {}, meta = {} }) {
    switch (type) {
        case 'persist/REHYDRATE': {
            return {
                ...state,
                pagination: {
                    ...initialState.initialPagination,
                },
                list: initialState.list,
            }
        }
        case 'API_CALL##FAILURE':
            return {
                ...state,
                error: payload,
                isWorking: false,
            }
        case 'FETCH_STORES##REQUEST':
        case 'FETCH_SINGLE_STORE##REQUEST':
        case 'FETCH_DEVICE##REQUEST':
        case StoreTypes.CREATE_STORE_REQUEST:
        case StoreTypes.UPDATE_STORE_REQUEST:
            return {
                ...state,
                isWorking: true,
            }
        case 'FETCH_STORES##SUCCESS': {
            // Meta information
            const devices = { ...state.devices } // Mapping object

            // If paginating, data comes inside `data` instead of directly in the payload
            const rawList = payload?.data || []

            // Process store list
            const list = rawList.map((store) => {
                const formattedStore = formatRawStoreInfo(store)
                const { devices: devicesList } = formattedStore

                // Add or update every device into mapping object
                devicesList?.forEach((device) => {
                    updateDeviceStatusInfo(devices, device, formattedStore)
                })

                // return store information
                return formattedStore
            })

            return {
                ...state,
                devices,
                devicesInit: true,
                isWorking: false,
                list,
                totalDevices: payload?.totalDevices || 0,
                totalFilteredDevices: payload?.filteredDevices || 0,
                pagination: {
                    ...state.pagination,
                    total: payload?.total || 0,
                },
            }
        }
        case StoreTypes.CREATE_STORE_SUCCESS: {
            const store = formatRawStoreInfo({
                devices: [],
                devices_count: 0,
                online_devices_count: 0,
                ...payload,
            })

            return {
                ...state,
                list: [...state.list, store],
                isWorking: false,
            }
        }
        case StoreTypes.DELETE_STORE_SUCCESS: {
            const { index } = meta

            return {
                ...state,
                list: [...state.list.slice(0, index), ...state.list.slice(index + 1)],
                isWorking: false,
            }
        }
        case StoreTypes.UPDATE_STORE_SUCCESS: {
            // Format payload data
            const store = formatRawStoreInfo(payload)
            const { selectedStore } = state
            const { index } = meta

            const newState = {
                ...state,
                isWorking: false,
            }

            // Update store list if index has been sent
            if (typeof index === 'number') {
                const { list } = state
                newState.list = ArrayUtils.insertItem(list, index, {
                    ...state.list[index],
                    ...store,
                })
            }

            // Update selectedStore if present
            if (selectedStore) {
                newState.selectedStore = {
                    ...(state.selectedStore || {}),
                    ...store,
                }
            }

            return newState
        }
        case 'FETCH_SINGLE_STORE##SUCCESS': {
            const store = formatRawStoreInfo(payload)
            const { devices: storeDevices } = store || {}
            const devices = { ...state.devices } // Mapping object

            // Append the online status from device mapping to the devices
            // for not to call the socket server again
            const updatedStoredevices = storeDevices.map((device) => {
                updateDeviceStatusInfo(devices, device, store)
                const { id } = device

                // The resulting device with updated online info
                return {
                    ...device,
                    ...devices[id],
                }
            })

            return {
                ...state,
                isWorking: false,
                devices,
                selectedStore: {
                    ...store,
                    devices: updatedStoredevices,
                },
            }
        }
        case StoreTypes.STORE_NOTE_SUCCESS: {
            if (!state.selectedStore) {
                return state
            }

            const { notes: oldNotes = [] } = state.selectedStore
            const { parentID } = meta
            let newNotes

            if (parentID) {
                // Get parent index by its id
                const parentNoteIndex = oldNotes.findIndex(({ id }) => id === parentID)

                // Get parent old information in state
                const parentNotes = oldNotes[parentNoteIndex] || {}

                // Get parent old list of children
                const { children: parentChildren = [] } = parentNotes

                // Create a new list with the old children plus the new one
                const newChildrenNotes = ArrayUtils.insertItem(parentChildren, parentChildren.length, payload).sort(StoreUtils.compareNoteDesc)

                // Create the new list of notes
                newNotes = ArrayUtils.insertItem(oldNotes, parentNoteIndex, {
                    ...parentNotes,
                    children: newChildrenNotes,
                }).sort(StoreUtils.compareNoteDesc)
            } else {
                // Insert the new note into the note list
                newNotes = ArrayUtils.insertItem(oldNotes, oldNotes.length, {
                    ...payload,
                    children: (payload.children || []).sort(StoreUtils.compareNoteDesc),
                }).sort(StoreUtils.compareNoteDesc)
            }

            // Get the notes back inside the current store
            return {
                ...state,
                isWorking: false,
                selectedStore: {
                    ...state.selectedStore,
                    notes: newNotes,
                },
            }
        }
        case StoreTypes.STORE_NOTE_IMAGE_UPLOADED: {
            if (!state.selectedStore) {
                return state
            }

            const { notes: oldNotes = [] } = state.selectedStore
            const { noteID, parentID } = meta
            let newNotes

            if (parentID) {
                // Get parent index by its id
                const parentNoteIndex = oldNotes.findIndex(({ id }) => id === parentID)

                // Get parent old information in state
                const parentNotes = oldNotes[parentNoteIndex] || {}

                // Get parent old list of children
                const { children: parentChildren = [] } = parentNotes

                // Get child note index
                const childNoteIndex = parentChildren.findIndex(({ id }) => id === noteID)

                // Get child note object
                const childNote = parentChildren[childNoteIndex] || {}

                // Get child note images array
                const { images: childImages = [] } = childNote

                // Create the new images array
                const newChildImages = ArrayUtils.insertItem(childImages, childImages.length, { ...payload })

                // Create new children notes array
                const newChildrenNotes = ArrayUtils.insertItem(parentChildren, childNoteIndex, {
                    ...childNote,
                    images: newChildImages,
                })

                // Create the new list of notes
                newNotes = ArrayUtils.insertItem(oldNotes, parentNoteIndex, {
                    ...parentNotes,
                    children: newChildrenNotes,
                })
            } else {
                // Get note index
                const noteIndex = oldNotes.findIndex(({ id }) => id === noteID)

                // Get note data
                const note = oldNotes[noteIndex] || {}

                // Get note's images array
                const { images: oldImages = [] } = note

                // Create new image array
                const newImageList = ArrayUtils.insertItem(oldImages, oldImages.length, {
                    ...payload,
                })

                // Insert the new note into the note list
                newNotes = ArrayUtils.insertItem(oldNotes, noteIndex, {
                    ...note,
                    images: newImageList,
                })
            }

            // Get the notes back inside the current store
            return {
                ...state,
                isWorking: false,
                selectedStore: {
                    ...state.selectedStore,
                    notes: newNotes,
                },
            }
        }
        case 'DEVICE_CONNECTION_SOCKET': {
            const { id } = payload
            const { devices } = state
            const { [id]: deviceStatus = {} } = devices

            return {
                ...state,
                devices: {
                    ...devices,
                    [id]: {
                        ...deviceStatus,
                        isOnline: true,
                        lastContact: DateUtils.now(),
                    },
                },
            }
        }
        case 'DEVICE_CONNECTION_SOCKET_ARRAY': {
            const { ids } = payload
            const { devices } = state

            return {
                ...state,
                devices: {
                    ...ids.reduce(
                        (out, id) => ({
                            ...out,
                            [id]: {
                                ...devices[id],
                                isOnline: true,
                                lastContact: DateUtils.now(),
                            },
                        }),
                        {}
                    ),
                },
            }
        }
        case SocketTypes.SOCKET_FETCH_CONNECTED_DEVICES_STORE_SUCCESS: {
            const { devices } = state

            return {
                ...state,
                devices: {
                    ...payload.reduce(
                        (out, id) => ({
                            ...out,
                            [id]: {
                                ...devices[id],
                                isOnline: true,
                                lastContact: DateUtils.now(),
                            },
                        }),
                        {}
                    ),
                },
            }
        }
        case 'DEVICE_DISCONNECTION_SOCKET': {
            const { id } = payload
            const { devices } = state
            const { [id]: deviceStatus = {} } = devices

            // Update online status and last contact time
            return {
                ...state,
                devices: {
                    ...devices,
                    [id]: {
                        ...deviceStatus,
                        isOnline: false,
                    },
                },
            }
        }
        case 'SCREENSHOT_RECEIVED': {
            return {
                ...state,
                screenshots: [...state.screenshots, { ...payload }],
            }
        }
        case 'SCREENSHOT_REMOVE': {
            return {
                ...state,
                screenshots: state.screenshots.filter(({ id }) => id !== payload.id),
            }
        }
        case 'DOWNLOAD_PERCENTAGE': {
            const { id, percentage } = payload
            const { devices } = state
            const { [id]: deviceStatus = {} } = devices
            const newPercentage = percentage === '100%' ? new Date().getTime() : percentage

            // Update lastDownload
            return {
                ...state,
                devices: {
                    ...devices,
                    [id]: {
                        ...deviceStatus,
                        lastDownload: newPercentage,
                    },
                },
            }
        }
        case 'CLEAR_SELETED_STORE': {
            return {
                ...state,
                selectedStore: null,
            }
        }
        case 'ATTACH_CHANNEL_DEVICE##SUCCESS':
        case 'DETACH_CHANNEL_DEVICE##SUCCESS':
        case 'FETCH_DEVICE##SUCCESS': {
            const { selectedStore: oldSelectedStore = {} } = state
            let selectedStore = oldSelectedStore

            // If there was an store selected, update its information
            // If not, do not mutate it
            if (oldSelectedStore) {
                const { devices: oldDevices = [] } = oldSelectedStore
                const devices = oldDevices.map(
                    (device) =>  {
                        if (device.id === payload.id) {
                            return {
                                ...device,
                                ...formatRawDeviceInfo(payload),
                            }
                        }
                        
                        return device
                    }
                )

                selectedStore = {
                    ...state.selectedStore,
                    devices,
                }
            }

            return {
                ...state,
                isWorking: false,
                selectedDevice: {
                    ...payload,
                },
                selectedStore,
            }
        }
        case AppTypes.SELECT_ESTATE: {
            return {
                ...state,
                devices: {
                    ...initialDevicesMap,
                },
                filters: {
                    ...initialFilters,
                },
                list: initialState.list,
                search: '',
            }
        }
        case DeviceTypes.DEVICE_UPDATE_SUCCESS: {
            const { selectedStore } = state
            const { devices = [], code } = selectedStore || {}
            const { id, store_code } = payload
            const index = devices.findIndex((device) => device.id === id)

            if (index === -1) {
                return state
            }

            // In case device has been moved out of selected state
            if (code !== store_code) {
                return {
                    ...state,
                    selectedStore: {
                        ...state.selectedStore,
                        devices: ArrayUtils.removeItem(devices, index),
                    },
                }
            }

            const updatedDevice = formatRawDeviceInfo(payload)

            return {
                ...state,
                selectedStore: {
                    ...state.selectedStore,
                    devices: ArrayUtils.insertItem(devices, index, updatedDevice),
                },
            }
        }
        case StoreTypes.EDIT_NOTE_SUCCESS: {
            const { index } = meta
            const {
                selectedStore: { notes },
            } = state
            const newNotes = StoreUtils.onlyOneSticky(payload, notes, index)
            return {
                ...state,
                isWorking: false,
                selectedStore: state.selectedStore && {
                    ...state.selectedStore,
                    notes: newNotes.sort(StoreUtils.compareNoteDesc),
                },
            }
        }
        case StoreTypes.DELETE_NOTE_SUCCESS: {
            if (!state.selectedStore) {
                return state
            }

            // Get note id and parent id if any
            const { id: noteID, parentID } = meta
            // Get store details and notes
            const {
                selectedStore,
                selectedStore: { notes: oldNotes = [] },
            } = state

            let newNotes

            // If deleted note had a parent
            if (parentID) {
                // Find parent index
                const parentIndex = oldNotes.findIndex(({ id }) => parentID === id)

                // Get parent
                const parent = oldNotes[parentIndex] || {}

                // Get parent's children
                const { children: parentChildren } = parent

                // Get note's index
                const childIndex = parentChildren.findIndex(({ id }) => noteID === id)

                // Remove note from parent's notes
                const newParentChildren = childIndex === -1 ? parentChildren : ArrayUtils.removeItem(parentChildren, childIndex)

                // Build the new parent
                const newParent = {
                    ...parent,
                    children: newParentChildren,
                }

                // Update parent in notes array
                newNotes = ArrayUtils.insertItem(oldNotes, parentIndex, newParent)
            } else {
                // Get note index in notes array
                const noteIndex = oldNotes.findIndex(({ id }) => noteID === id)

                // Remove note from index
                newNotes = ArrayUtils.removeItem(oldNotes, noteIndex)
            }

            return {
                ...state,
                isWorking: false,
                selectedStore: {
                    ...selectedStore,
                    notes: newNotes,
                },
            }
        }
        case StoreTypes.CHANGE_STORE_FILTERS: {
            if (payload.filters)
                return {
                    ...state,
                    filters: payload.filters,
                }
            return {
                ...state,
                filters: { ...initialFilters },
                search: '',
            }
        }
        case StoreTypes.CHANGE_STORE_SEARCH_STRING: {
            // If state was empty and now it has some value
            if (!state.search && payload.search) {
                return {
                    ...state,
                    search: payload.search,
                    filters: {
                        ...state.filters,
                        groupsToFilter: [], // Search for all kind of device's groups
                        showEmptyStores: true, // Look for empty stores
                    },
                }
            }

            // If store search was set and now it is removed
            if (state.search && !payload.search) {
                // If user had selected a group filter, keep it.
                // If not, look for installed devices only
                const groupsToFilter = state.filters.groupsToFilter.length ? state.filters.groupsToFilter : initialFilters.groupsToFilter

                return {
                    ...state,
                    search: '',
                    filters: {
                        ...state.filters,
                        groupsToFilter,
                        showEmptyStores: initialFilters.showEmptyStores,
                    },
                }
            }

            return {
                ...state,
                search: payload.search,
            }
        }
        case StoreTypes.CHANGE_DEVICES:
            return {
                ...state,
                selectedStore: {
                    ...state.selectedStore,
                    devices: payload,
                },
            }
        
        case StoreTypes.DESELECT_STORE: {
            return {
                ...state,
                selectedStore: initialState.selectedStore,
            }
        }
        default:
            return state
    }
}

/**
 * Get selected store
 * @param {Object} state State object
 */
function getSelectedStore(state) {
    return state[STATE_KEY].selectedStore
}

/**
 * Get devices from selected store
 * @param {Object} state
 */
function getSelectedStoreDevices(state) {
    const selectedStore = getSelectedStore(state) || {}

    return selectedStore?.devices || []
}

/**
 * Select and format data for the selected store. It includes information
 * about the connection of the devices in that store
 * @param {import('.').RootState} state
 * @returns 
 */
function selectStoreWithDevices(state) {
    const { devices: devicesStatus, selectedStore } = state[STATE_KEY]

    // No store selected
    if (!selectedStore) {
        return null
    }

    const { devices: storeDevices = [] } = selectedStore

    // Extend information for every device in this store
    const devices = storeDevices
        .map((dev) => {
            const { id } = dev
            return {
                ...dev,
                // Merge socket connection data with device data
                ...(devicesStatus[id] || {}),
            }
        })

        // Calculate store's offline percentage
    const offline = storeOfflinePercentage({ devices })

    return {
        ...selectedStore,
        devices,
        offline,
    }
}

export default reducer

export {
    areFiltersAtDefaultValue,
    getSelectedStoreDevices,
    isDeviceOnline,
    selectStoreWithDevices,
    storeOfflinePercentage,
}
